// "Serves" on a menu item (backend migration 135): how many people one of it
// feeds. `food.serves` is the item's own number (shown on its card, like
// ezCater's "$150.00 | Serves 10"); each variation can carry its own for an
// item priced by size.

const MAX_QUANTITY = 50;

const whole = (v) => {
  const n = Number(v);
  return Number.isInteger(n) && n > 0 ? n : null;
};

/** People one of this size feeds: its own number, else the item's. */
export function servesOfVariation(variation, food) {
  return whole(variation?.serves) ?? whole(food?.serves);
}

/** "Serves 10" — the item's number, else the range across its sizes. */
export function servesLabel(food) {
  const own = whole(food?.serves);
  if (own) return `Serves ${own}`;
  const all = (food?.variations || []).map((v) => whole(v.serves)).filter(Boolean);
  if (!all.length) return null;
  const min = Math.min(...all);
  const max = Math.max(...all);
  return min === max ? `Serves ${min}` : `Serves ${min}–${max}`;
}

/** "Serves 10" for one size, only when that size has its own number. */
export function variationServesLabel(variation) {
  const n = whole(variation?.serves);
  return n ? `Serves ${n}` : null;
}

/**
 * How to feed `guests` with this item: per size with a serves number (or the
 * item's own number on its cheapest size), how many it takes, rounded up; then
 * the size that leaves the fewest spare portions, and of those the cheaper
 * total. Null when nothing is set.
 * → { variation, serves, quantity, feeds, total }
 */
export function suggestFor(food, guests) {
  const people = Math.round(Number(guests) || 0);
  if (people < 1) return null;
  const variations = food?.variations || [];
  let options = variations.filter((v) => whole(v.serves)).map((v) => ({ variation: v, serves: whole(v.serves) }));
  if (!options.length && whole(food?.serves) && variations.length) {
    const cheapest = variations.slice().sort((a, b) => (parseFloat(a.price) || 0) - (parseFloat(b.price) || 0))[0];
    options = [{ variation: cheapest, serves: whole(food.serves) }];
  }
  let best = null;
  for (const { variation, serves } of options) {
    const quantity = Math.ceil(people / serves);
    if (quantity > MAX_QUANTITY) continue;
    const feeds = quantity * serves;
    const total = quantity * (parseFloat(variation.price) || 0);
    const spare = feeds - people;
    if (!best || spare < best.spare || (spare === best.spare && total < best.total)) {
      best = { variation, serves, quantity, feeds, total, spare };
    }
  }
  if (!best) return null;
  const { spare, ...out } = best;
  return out;
}
